import { gql } from '@apollo/client';

// Get user's current billing information
export const GET_USER_BILLING = gql`
  query GetUserBilling {
    getUserBilling {
      planId
      status
      currentPeriodEnd
      cancelAtPeriodEnd
      changePlanAtPeriodEnd
      nextPlanId
      currency
      subscriptionId
      providerCustomerId
      providerSubscriptionId
      monerisSubscriptionId
      monerisRecurringId
      dataKey
      revisionCount
      gracePeriod {
        startDate
        endDate
        reason
      }
      createdAt
      updatedAt
    }
  }
`;

// Initialize user billing
export const INITIALIZE_USER_BILLING = gql`
  mutation InitializeUserBilling($userId: String!, $planId: String) {
    initializeUserBilling(userId: $userId, planId: $planId) {
      planId
      status
      currentPeriodEnd
      cancelAtPeriodEnd
      currency
      subscriptionId
      createdAt
      updatedAt
    }
  }
`;

// Get all available subscription plans. Sizes are bytes (binary: 1 GB =
// 1024^3); -1 means unlimited. uploadSize is the largest single file.
// includes: what the plan unlocks, from the same rule the API enforces.
export const GET_SUBSCRIPTION_PLANS = gql`
  query GetSubscriptionPlans {
    getSubscriptionPlans {
      id
      name
      price
      limits {
        storage
        projects
        revisions
        uploadSize
      }
      features
      popular
      includes {
        transcripts
        suggestCuts
        aiTaskSuggestions
        quickEdits
      }
    }
  }
`;

// Whether the Stripe Checkout the user came back from went through:
// applied (the account shows it), paid (the webhook hasn't landed yet), open,
// expired or unknown.
export const CHECKOUT_STATUS = gql`
  query CheckoutStatus($sessionId: String!) {
    checkoutStatus(sessionId: $sessionId) {
      state
      planId
    }
  }
`;

// Get user's current usage statistics
export const GET_USAGE_STATS = gql`
  query GetUsageStats {
    getUsageStats {
      storage {
        used
        limit
        percentage
        nearLimit
      }
      projects {
        used
        limit
        percentage
        nearLimit
      }
      revisions {
        used
        limit
        percentage
        nearLimit
      }
    }
  }
`;

// Get plan change history for the user
export const GET_PLAN_CHANGE_HISTORY = gql`
  query GetPlanChangeHistory($limit: Int) {
    getPlanChangeHistory(limit: $limit) {
      userId
      changes {
        id
        oldPlanId
        newPlanId
        timestamp
        prorationDetails {
          additionalCharge
          creditForNextBilling
          currency
          daysRemaining
          simpleExplanation
          technicalBreakdown
        }
        immediate
        reason
        success
      }
      totalCount
    }
  }
`;

// Change subscription plan
export const CHANGE_PLAN = gql`
  mutation ChangePlan($userId: String!, $newPlanId: String!) {
    changePlan(userId: $userId, newPlanId: $newPlanId) {
      success
      oldPlanId
      newPlanId
      prorationDetails {
        additionalCharge
        creditForNextBilling
        currency
        daysRemaining
        simpleExplanation
        technicalBreakdown
      }
      newSubscriptionId
      immediateAccess
      nextBillingDate
      error
    }
  }
`;

// Cancel subscription (OLD - for one-time payments)
export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($userId: String!) {
    cancelSubscription(userId: $userId)
  }
`;

// Reactivate cancelled subscription (OLD - for one-time payments)
export const REACTIVATE_SUBSCRIPTION = gql`
  mutation ReactivateSubscription($userId: String!) {
    reactivateSubscription(userId: $userId)
  }
`;

// ========================================
// MONERIS-MANAGED SUBSCRIPTION MUTATIONS
// ========================================

// Step 1: Create tokenization session for secure card collection with recurring setup
export const CREATE_TOKENIZATION_SESSION = gql`
  mutation CreateTokenizationSession($userId: String!, $planId: String, $amount: Float) {
    createTokenizationSession(userId: $userId, planId: $planId, amount: $amount) {
      success
      ticketId
      tokenUrl
      error
    }
  }
`;

// Step 2: Create recurring subscription with automatic monthly billing
export const CREATE_MONERIS_SUBSCRIPTION = gql`
  mutation CreateMonerisSubscription($input: CreateMonerisSubscriptionInput!) {
    createMonerisSubscription(input: $input) {
      success
      subscriptionId
      monerisRecurringId
      message
      code
      error
    }
  }
`;

// Upgrade subscription with immediate proration
export const UPGRADE_MONERIS_SUBSCRIPTION = gql`
  mutation UpgradeMonerisSubscription($input: UpdateMonerisSubscriptionInput!) {
    upgradeMonerisSubscription(input: $input) {
      success
      prorationAmount
      message
      code
      error
    }
  }
`;

// Downgrade subscription (scheduled for period end)
export const DOWNGRADE_MONERIS_SUBSCRIPTION = gql`
  mutation DowngradeMonerisSubscription($input: UpdateMonerisSubscriptionInput!) {
    downgradeMonerisSubscription(input: $input) {
      success
      effectiveDate
      message
      error
    }
  }
`;

// Cancel Moneris subscription (access until period end)
export const CANCEL_MONERIS_SUBSCRIPTION = gql`
  mutation CancelMonerisSubscription($userId: String!) {
    cancelMonerisSubscription(userId: $userId) {
      success
      accessUntil
      message
      error
    }
  }
`;

// Reactivate cancelled Moneris subscription
export const REACTIVATE_MONERIS_SUBSCRIPTION = gql`
  mutation ReactivateMonerisSubscription($userId: String!) {
    reactivateMonerisSubscription(userId: $userId) {
      success
      message
      error
    }
  }
`;


// Create hosted checkout session for Moneris payment
export const CREATE_CHECKOUT_SESSION = gql`
  mutation CreateCheckoutSession($planId: String!, $currency: String) {
    createCheckoutSession(planId: $planId, currency: $currency) {
      success
      checkoutUrl
      sessionId
      ticket
      error
    }
  }
`;

// Confirm payment and update plan
export const CONFIRM_PAYMENT_AND_UPDATE_PLAN = gql`
  mutation ConfirmPaymentAndUpdatePlan($planId: String!, $paymentResponse: String!) {
    confirmPaymentAndUpdatePlan(planId: $planId, paymentResponse: $paymentResponse) {
      success
      message
      newPlanId
      error
    }
  }
`;


// Get billing invoices
export const GET_BILLING_INVOICES = gql`
  query GetBillingInvoices($limit: Int) {
    getUserInvoices(limit: $limit) {
      id
      invoiceNumber
      userId
      amount
      currency
      status
      createdAt
      downloadUrl
    }
  }
`;









// Recurring Billing Queries
export const GET_RECURRING_SUBSCRIPTION = gql`
  query GetRecurringSubscription($userId: String!) {
    getRecurringSubscription(userId: $userId) {
      subscriptionId
      recurringId
      status
      billingAmount
      currency
      nextBillingDate
      lastPaymentDate
      totalPayments
      failedPaymentCount
      isRecurring
    }
  }
`;

// Recurring Billing Mutations
export const CREATE_RECURRING_SUBSCRIPTION = gql`
  mutation CreateRecurringSubscription($input: CreateRecurringSubscriptionInput!) {
    createRecurringSubscription(input: $input) {
      success
      subscriptionId
      transactionId
      amount
      currency
      message
      error
    }
  }
`;

export const UPGRADE_RECURRING_SUBSCRIPTION = gql`
  mutation UpgradeRecurringSubscription($input: UpgradeRecurringSubscriptionInput!) {
    upgradeRecurringSubscription(input: $input) {
      success
      message
      subscriptionId
      recurringId
      oldPlanId
      newPlanId
      prorationAmount
      nextBillingDate
      nextBillingAmount
      currency
      effectiveDate
      error
    }
  }
`;

export const SCHEDULE_DOWNGRADE = gql`
  mutation ScheduleDowngrade($input: ScheduleDowngradeInput!) {
    scheduleDowngrade(input: $input) {
      success
      message
      currentPlanId
      scheduledPlanId
      effectiveDate
      refundAmount
      currency
      gracePeriodEnd
      error
    }
  }
`;

export const CANCEL_RECURRING_SUBSCRIPTION = gql`
  mutation CancelRecurringSubscription($input: CancelRecurringSubscriptionInput!) {
    cancelRecurringSubscription(input: $input) {
      success
      message
      subscriptionId
      recurringId
      cancelledDate
      accessUntil
      refundAmount
      currency
      error
    }
  }
`;

export const REACTIVATE_RECURRING_SUBSCRIPTION = gql`
  mutation ReactivateRecurringSubscription($input: ReactivateRecurringSubscriptionInput!) {
    reactivateRecurringSubscription(input: $input) {
      success
      message
      subscriptionId
      recurringId
      reactivatedDate
      nextBillingDate
      planId
      billingAmount
      currency
      error
    }
  }
`;

// ============================================================================
// Provider-neutral billing (Phase 3c)
//
// These mirror the backend mutations added in Phase 2 + 3b. They are
// additive — existing Moneris-specific exports above remain unchanged so
// the legacy flow in BillingDashboard keeps working. Dashboard cutover to
// these endpoints lands in Phase 3c2.
//
// Launch unions: Stripe flows return a StripeCheckoutLaunch (redirect URL);
// Moneris flows return a MonerisModalLaunch (modal ticket). Consumers
// branch on __typename or the `kind` field — see BillingProviderLauncher.
// ============================================================================

export const GET_ACTIVE_PAYMENT_PROVIDER = gql`
  query GetActivePaymentProvider {
    activePaymentProvider
  }
`;

// Fragment reused across every mutation that returns a BillingLaunch so
// the union shape stays consistent and changes only need to be made once.
const BILLING_LAUNCH_FIELDS = gql`
  fragment BillingLaunchFields on BillingLaunch {
    __typename
    ... on StripeCheckoutLaunch {
      kind
      provider
      sessionId
      url
      mode
      expiresAt
    }
    ... on MonerisModalLaunch {
      kind
      provider
      ticket
      checkoutId
      environment
    }
  }
`;

export const START_SUBSCRIPTION = gql`
  ${BILLING_LAUNCH_FIELDS}
  mutation StartSubscription($planId: String!, $currency: String!) {
    startSubscription(planId: $planId, currency: $currency) {
      success
      error
      launch {
        ...BillingLaunchFields
      }
    }
  }
`;

export const START_PAYMENT_METHOD_UPDATE = gql`
  ${BILLING_LAUNCH_FIELDS}
  mutation StartPaymentMethodUpdate($returnUrl: String) {
    startPaymentMethodUpdate(returnUrl: $returnUrl) {
      success
      provider
      message
      error
      launch {
        ...BillingLaunchFields
      }
    }
  }
`;

// Shared result shape for subscription lifecycle mutations in Phase 3b
// (upgradeMyPlan / scheduleMyDowngrade / cancelMySubscription /
// reactivateMySubscription). success, provider, planId, effectiveAt,
// message, error.
const SUBSCRIPTION_MUTATION_RESULT_FIELDS = gql`
  fragment SubscriptionMutationResultFields on SubscriptionMutationResult {
    success
    provider
    planId
    effectiveAt
    message
    error
  }
`;

export const UPGRADE_MY_PLAN = gql`
  ${SUBSCRIPTION_MUTATION_RESULT_FIELDS}
  mutation UpgradeMyPlan($newPlanId: String!) {
    upgradeMyPlan(newPlanId: $newPlanId) {
      ...SubscriptionMutationResultFields
    }
  }
`;

export const SCHEDULE_MY_DOWNGRADE = gql`
  ${SUBSCRIPTION_MUTATION_RESULT_FIELDS}
  mutation ScheduleMyDowngrade($newPlanId: String!) {
    scheduleMyDowngrade(newPlanId: $newPlanId) {
      ...SubscriptionMutationResultFields
    }
  }
`;

export const CANCEL_MY_SUBSCRIPTION = gql`
  ${SUBSCRIPTION_MUTATION_RESULT_FIELDS}
  mutation CancelMySubscription($reason: String) {
    cancelMySubscription(reason: $reason) {
      ...SubscriptionMutationResultFields
    }
  }
`;

export const REACTIVATE_MY_SUBSCRIPTION = gql`
  ${SUBSCRIPTION_MUTATION_RESULT_FIELDS}
  mutation ReactivateMySubscription {
    reactivateMySubscription {
      ...SubscriptionMutationResultFields
    }
  }
`;
